import React, { useEffect, useMemo, useState } from "react";

import {
  CheckCircle,
  Plus,
  Trash,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  machineCatalog as machineCatalogApi,
  prospectOpportunities as opportunitiesApi,
  quotes as quotesApi,
} from "@/api";
import { formatApiError } from "@/lib/api";

const PAYMENT_METHOD_OPTIONS = [
  ["", "À définir"],
  ["bank_transfer", "Virement"],
  ["card", "Carte"],
  ["cheque", "Chèque"],
  ["cash", "Espèces"],
  ["bank_financing", "Financement bancaire"],
  ["leasing", "Leasing"],
  ["third_party", "Tiers financeur"],
  ["other", "Autre"],
];

const BUSINESS_PROCESS_OPTIONS = [
  ["goods", "Biens"],
  ["services", "Services"],
  ["mixed", "Biens et services"],
];

const emptyLine = () => ({
  catalogItemId: "",
  section: "Produit Principal",
  reference: "",
  description: "",
  quantity: 1,
  unit: "Unité(s)",
  unitPrice: "",
  discountRate: 0,
  vatRate: 20,
  salesClassificationCode: "",
  purchaseUnitPrice: 0,
  notes: "",
});

const plusDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const lockedQuoteStatuses = new Set([
  "pending_validation",
  "accepted",
  "purchase_order_created",
]);

const revisionStatuses = new Set([
  "approved",
  "sent",
  "refused",
]);

function normalizeLines(opportunity) {
  const lines = Array.isArray(opportunity?.items)
    ? opportunity.items
    : [];

  return lines.length
    ? lines.map((item) => ({
        ...emptyLine(),
        ...item,
        catalogItemId:
          item.catalogItemId ||
          item.catalog_item_id ||
          "",
        unitPrice:
          item.unitPrice ??
          item.unit_price ??
          "",
        discountRate:
          item.discountRate ??
          item.discount_rate ??
          0,
        vatRate:
          item.vatRate ??
          item.vat_rate ??
          20,
        salesClassificationCode:
          item.salesClassificationCode ??
          item.sales_classification_code ??
          "",
        purchaseUnitPrice:
          item.purchaseUnitPrice ??
          item.purchase_unit_price ??
          0,
      }))
    : [emptyLine()];
}

export default function OpportunityQuoteSubmitModal({
  opportunity,
  onClose,
  onSubmitted,
}) {
  const [lines, setLines] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [validUntil, setValidUntil] = useState(plusDays(30));
  const [notes, setNotes] = useState("");
  const [evolizOrganizationCode, setEvolizOrganizationCode] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("Paiement à définir");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [businessProcess, setBusinessProcess] = useState("mixed");
  const [globalDiscountRate, setGlobalDiscountRate] = useState(0);
  const [revisionReason, setRevisionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!opportunity) return;

    setLines(normalizeLines(opportunity));
    setValidUntil(plusDays(30));
    setNotes(opportunity.comment || "");
    setEvolizOrganizationCode(
      opportunity.evolizOrganizationCode || ""
    );
    setPaymentTerms(
      opportunity.paymentTerms || "Paiement à définir"
    );
    setPaymentMethod(
      opportunity.payment?.method || ""
    );
    setBusinessProcess(
      opportunity.businessProcess || "mixed"
    );
    setGlobalDiscountRate(
      Number(opportunity.globalDiscountRate || 0)
    );
    setRevisionReason("");
  }, [opportunity]);

  useEffect(() => {
    if (!opportunity) return undefined;

    let cancelled = false;

    machineCatalogApi
      .list()
      .then((items) => {
        if (!cancelled) {
          setCatalog(
            Array.isArray(items)
              ? items.filter((item) => item.active !== false)
              : []
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Le catalogue n’a pas pu être chargé.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [opportunity]);

  const totals = useMemo(
    () =>
      lines.reduce(
        (result, line) => {
          const quantity = Number(line.quantity || 0);
          const unitPrice = Number(line.unitPrice || 0);
          const vatRate = Number(line.vatRate || 0);
          const discountRate = Math.min(
            Math.max(Number(line.discountRate || 0), 0),
            100
          );
          const grossLineHt = quantity * unitPrice;
          const lineHt =
            grossLineHt * (1 - discountRate / 100);

          return {
            ht: result.ht + lineHt,
            vat: result.vat + lineHt * vatRate / 100,
          };
        },
        { ht: 0, vat: 0 }
      ),
    [lines]
  );

  if (!opportunity) return null;

  const normalizedGlobalDiscountRate = Math.min(
    Math.max(Number(globalDiscountRate || 0), 0),
    100
  );
  const totalHtAfterGlobalDiscount =
    totals.ht * (1 - normalizedGlobalDiscountRate / 100);
  const totalVatAfterGlobalDiscount =
    totals.vat * (1 - normalizedGlobalDiscountRate / 100);

  const quoteStatus =
    opportunity.primaryQuoteStatus ||
    opportunity.quoteWorkflowStatus ||
    "";
  const locked = lockedQuoteStatuses.has(quoteStatus);
  const needsRevisionReason = revisionStatuses.has(quoteStatus);

  const updateLine = (index, patch) => {
    setLines((current) =>
      current.map((line, lineIndex) =>
        lineIndex === index
          ? { ...line, ...patch }
          : line
      )
    );
  };

  const chooseCatalog = (index, catalogItemId) => {
    const item = catalog.find(
      (candidate) => candidate.id === catalogItemId
    );

    updateLine(index, {
      catalogItemId,
      description: item?.name || "",
      reference: item?.reference || "",
    });
  };

  const submit = async (event) => {
    event.preventDefault();

    const cleanLines = lines.filter(
      (line) => String(line.description || "").trim()
    );

    if (!cleanLines.length) {
      toast.error("Ajoutez au moins un article ou une prestation.");
      return;
    }

    if (
      cleanLines.some(
        (line) =>
          !(Number(line.quantity) > 0) ||
          Number(line.unitPrice) < 0 ||
          Number(line.vatRate) < 0 ||
          Number(line.vatRate) > 100
      )
    ) {
      toast.error("Vérifiez les quantités, prix et taux de TVA.");
      return;
    }

    if (!(totalHtAfterGlobalDiscount > 0)) {
      toast.error("Le total HT doit être supérieur à zéro.");
      return;
    }

    if (
      cleanLines.some(
        (line) =>
          Number(line.discountRate || 0) < 0 ||
          Number(line.discountRate || 0) > 100 ||
          Number(line.purchaseUnitPrice || 0) < 0
      )
    ) {
      toast.error("Vérifiez les remises et prix d'achat HT.");
      return;
    }

    if (
      Number(globalDiscountRate || 0) < 0 ||
      Number(globalDiscountRate || 0) > 100
    ) {
      toast.error("La remise globale doit être comprise entre 0 et 100 %.");
      return;
    }

    if (needsRevisionReason && !revisionReason.trim()) {
      toast.error("Indiquez le motif de la nouvelle version.");
      return;
    }

    setSubmitting(true);

    try {
      await opportunitiesApi.update(opportunity.id, {
        title: opportunity.title || "Proposition commerciale",
        product_name: cleanLines
          .map((line) => line.description)
          .join(" + "),
        items: cleanLines.map((line, index) => ({
          catalog_item_id: line.catalogItemId || null,
          section: line.section || "Produit Principal",
          reference: line.reference || "",
          description: line.description.trim(),
          quantity: Number(line.quantity),
          unit: line.unit || "Unité(s)",
          unit_price: Number(line.unitPrice || 0),
          discount_rate: Number(line.discountRate || 0),
          vat_rate: Number(line.vatRate || 20),
          sales_classification_code:
            line.salesClassificationCode || "",
          purchase_unit_price: Number(
            line.purchaseUnitPrice || 0
          ),
          notes: line.notes || "",
          order: index,
          source: "human",
        })),
        estimated_amount: totalHtAfterGlobalDiscount,
        status: opportunity.stage || opportunity.status || "opportunity",
        expected_purchase_date:
          opportunity.expectedPurchaseDate ||
          opportunity.expectedCloseDate ||
          "",
        next_action:
          opportunity.nextAction ||
          "",
        next_action_at:
          opportunity.nextActionAt ||
          "",
        comment: notes,
        evoliz_organization_code:
          evolizOrganizationCode.trim(),
        payment_terms: paymentTerms.trim(),
        business_process: businessProcess,
        global_discount_rate:
          normalizedGlobalDiscountRate,
        payment: {
          ...(opportunity.payment || {}),
          method: paymentMethod || null,
        },
      });

      const result = await quotesApi.createFromOpportunity(
        opportunity.id,
        {
          submit_for_validation: true,
          valid_until: validUntil,
          notes,
          evoliz_organization_code:
            evolizOrganizationCode.trim(),
          payment_terms: paymentTerms.trim(),
          payment_method: paymentMethod,
          business_process: businessProcess,
          global_discount_rate:
            normalizedGlobalDiscountRate,
          revision_reason: revisionReason.trim(),
        }
      );

      toast.success(
        result.createdRevision
          ? "Nouvelle version créée et envoyée en validation."
          : "Devis créé et envoyé en validation."
      );

      await onSubmitted?.(result);
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) ||
          "Impossible de préparer le devis."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center acta-modal-overlay p-3 sm:p-4"
      onMouseDown={() => {
        if (!submitting) onClose?.();
      }}
      data-testid="opportunity-quote-submit-modal"
    >
      <form
        onSubmit={submit}
        onMouseDown={(event) => event.stopPropagation()}
        className="acta-modal-surface acta-modal-scroll max-h-[calc(100dvh-12px)] w-full rounded-[28px] bg-[var(--do-bg-solid)] shadow-2xl sm:max-w-4xl sm:rounded-[28px]"
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b bg-[var(--do-bg-solid)] px-4 py-4 sm:px-6">
          <div>
            <div className="label-eyebrow">
              Proposition commerciale vivante
            </div>
            <h2 className="mt-1 font-heading text-xl font-black sm:text-2xl">
              {opportunity.title || "Préparer le devis"}
            </h2>
            <p className="mt-1 text-sm text-[var(--do-muted)]">
              Modifiez ici la proposition. Acta figera ensuite une version officielle sans ressaisie.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
            aria-label="Fermer"
          >
            <X size={18} weight="bold" />
          </button>
        </header>

        <div className="grid gap-5 px-4 py-5 sm:px-6 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="space-y-4">
            {lines.map((line, index) => (
              <section
                key={line.id || index}
                className="rounded-2xl border border-[var(--do-border)] p-4"
                data-testid={`opportunity-proposal-line-${index}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <strong>Ligne {index + 1}</strong>
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setLines((current) =>
                          current.filter(
                            (_, lineIndex) =>
                              lineIndex !== index
                          )
                        )
                      }
                      className="text-[var(--do-accent)]"
                      aria-label={`Retirer la ligne ${index + 1}`}
                    >
                      <Trash size={17} />
                    </button>
                  )}
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="sm:col-span-2">
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Produit du catalogue
                    </span>
                    <select
                      value={line.catalogItemId}
                      onChange={(event) =>
                        chooseCatalog(index, event.target.value)
                      }
                      className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                    >
                      <option value="">
                        Prestation libre
                      </option>
                      {catalog.map((item) => (
                        <option
                          key={item.id}
                          value={item.id}
                        >
                          {item.name} — {item.reference || "Sans référence"}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="sm:col-span-2">
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Description *
                    </span>
                    <input
                      required
                      value={line.description}
                      readOnly={Boolean(line.catalogItemId)}
                      onChange={(event) =>
                        updateLine(index, {
                          description: event.target.value,
                        })
                      }
                      className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                    />
                  </label>

                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Quantité
                    </span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={line.quantity}
                      onChange={(event) =>
                        updateLine(index, {
                          quantity: event.target.value,
                        })
                      }
                      className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                    />
                  </label>

                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Prix unitaire HT
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unitPrice}
                      onChange={(event) =>
                        updateLine(index, {
                          unitPrice: event.target.value,
                        })
                      }
                      className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                    />
                  </label>

                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      TVA %
                    </span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={line.vatRate}
                      onChange={(event) =>
                        updateLine(index, {
                          vatRate: event.target.value,
                        })
                      }
                      className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                    />
                  </label>

                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Remise %
                    </span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={line.discountRate}
                      onChange={(event) =>
                        updateLine(index, {
                          discountRate: event.target.value,
                        })
                      }
                      className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                    />
                  </label>

                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Code classification vente
                    </span>
                    <input
                      value={line.salesClassificationCode}
                      onChange={(event) =>
                        updateLine(index, {
                          salesClassificationCode:
                            event.target.value,
                        })
                      }
                      className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                    />
                  </label>

                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Prix d'achat HT
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.purchaseUnitPrice}
                      onChange={(event) =>
                        updateLine(index, {
                          purchaseUnitPrice: event.target.value,
                        })
                      }
                      className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                    />
                  </label>

                  <div className="rounded-xl bg-[var(--do-bg-solid)] p-3">
                    <div className="text-xs font-bold text-[var(--do-muted)]">
                      Total ligne HT
                    </div>
                    <div className="mt-1 font-black">
                      {(
                        Number(line.quantity || 0) *
                        Number(line.unitPrice || 0) *
                        (1 - Math.min(
                          Math.max(Number(line.discountRate || 0), 0),
                          100
                        ) / 100)
                      ).toFixed(2)} €
                    </div>
                  </div>
                </div>
              </section>
            ))}

            <button
              type="button"
              onClick={() =>
                setLines((current) => [
                  ...current,
                  emptyLine(),
                ])
              }
              className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-black"
            >
              <Plus size={17} />
              Ajouter une ligne
            </button>

            <section className="grid gap-3 rounded-2xl border border-[var(--do-border)] p-4 sm:grid-cols-2">
              <label>
                <span className="text-xs font-bold text-[var(--do-muted)]">
                  Code organisation Evoliz
                </span>
                <input
                  value={evolizOrganizationCode}
                  onChange={(event) =>
                    setEvolizOrganizationCode(event.target.value)
                  }
                  placeholder="Optionnel pour le moment"
                  className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                />
              </label>

              <label>
                <span className="text-xs font-bold text-[var(--do-muted)]">
                  Conditions de règlement
                </span>
                <input
                  value={paymentTerms}
                  onChange={(event) =>
                    setPaymentTerms(event.target.value)
                  }
                  className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                />
              </label>

              <label>
                <span className="text-xs font-bold text-[var(--do-muted)]">
                  Mode de paiement
                </span>
                <select
                  value={paymentMethod}
                  onChange={(event) =>
                    setPaymentMethod(event.target.value)
                  }
                  className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                >
                  {PAYMENT_METHOD_OPTIONS.map(([value, label]) => (
                    <option key={value || "undefined"} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span className="text-xs font-bold text-[var(--do-muted)]">
                  Nature des opérations
                </span>
                <select
                  value={businessProcess}
                  onChange={(event) =>
                    setBusinessProcess(event.target.value)
                  }
                  className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                >
                  {BUSINESS_PROCESS_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span className="text-xs font-bold text-[var(--do-muted)]">
                  Remise globale %
                </span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={globalDiscountRate}
                  onChange={(event) =>
                    setGlobalDiscountRate(event.target.value)
                  }
                  className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                />
              </label>
            </section>

            <label className="block">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Notes et conditions
              </span>
              <textarea
                rows={4}
                value={notes}
                onChange={(event) =>
                  setNotes(event.target.value)
                }
                className="mt-1 w-full rounded-2xl border px-4 py-3 text-sm"
              />
            </label>

            {needsRevisionReason && (
              <label className="block">
                <span className="text-xs font-bold text-[var(--do-ink)]">
                  Motif de la nouvelle version *
                </span>
                <textarea
                  required
                  rows={3}
                  value={revisionReason}
                  onChange={(event) =>
                    setRevisionReason(event.target.value)
                  }
                  placeholder="Ex. négociation du prix, produit remplacé, conditions modifiées…"
                  className="mt-1 w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm"
                />
              </label>
            )}
          </div>

          <aside className="h-fit rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-4 lg:sticky lg:top-24">
            <div className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--do-muted)]">
              Synthèse
            </div>
            <div className="mt-3 font-heading text-2xl font-black">
              {totalHtAfterGlobalDiscount.toFixed(2)} € HT
            </div>
            <div className="mt-1 text-sm text-[var(--do-muted)]">
              {(totalHtAfterGlobalDiscount + totalVatAfterGlobalDiscount).toFixed(2)} € TTC
            </div>

            <label className="mt-5 block">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Validité du devis
              </span>
              <input
                type="date"
                value={validUntil}
                onChange={(event) =>
                  setValidUntil(event.target.value)
                }
                className="mt-1 min-h-[44px] w-full rounded-xl border bg-[var(--do-bg-solid)] px-3 text-sm"
              />
            </label>

            <div className="mt-5 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3 text-xs leading-5 text-[var(--do-muted)]">
              La version officielle sera immuable après envoi. Toute négociation ultérieure créera une nouvelle version historisée et taguée IA.
            </div>

            {locked && (
              <div className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-3 text-sm font-bold text-[var(--do-ink)]">
                {quoteStatus === "pending_validation"
                  ? "Le devis est déjà en attente de validation."
                  : "Le devis accepté est figé."}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || locked}
              className="mt-5 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-[var(--do-accent-strong)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
              data-testid="opportunity-submit-quote"
            >
              <CheckCircle size={18} weight="bold" />
              {submitting
                ? "Envoi…"
                : needsRevisionReason
                  ? "Créer la version et valider"
                  : quoteStatus === "draft" ||
                      quoteStatus === "rejected"
                    ? "Mettre à jour et soumettre"
                    : "Créer et soumettre à l’admin"}
            </button>
          </aside>
        </div>
      </form>
    </div>
  );
}
